import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import {
	MagnifyingGlassIcon,
	AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import {
	DataTable,
	StatusBadge,
	PriorityBadge,
	FilterDropdown,
	TextStack,
	Badge,
} from '../../components/nextgen';

import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';

import { getTickets } from '../../api';
import { getUserFullName } from '../../components/helpers/functions';

// Stat Card
const StatCard = ({ label, value, valueColor = 'text-gray-900' }) => (
	<div className="bg-white border border-gray-200 rounded-lg p-5">
		<p className="text-sm text-gray-500 mb-1">{label}</p>
		<p className={`text-3xl font-semibold ${valueColor}`}>{value}</p>
	</div>
);

const TicketsBeta = () => {
	const navigate = useNavigate();
	const { id: ticketIdFromUrl } = useParams();
	const [searchParams] = useSearchParams();
	const assetIdFromUrl = searchParams.get('assetId');
	const statusFromUrl = searchParams.get('status');
	const { setOpen: openTicketOverview } = useModal('ticketOverview');

	// Track if screen is xl or larger (1280px+)
	const [isXl, setIsXl] = useState(
		typeof window !== 'undefined' ? window.innerWidth >= 1280 : true,
	);

	useEffect(() => {
		const handleResize = () => setIsXl(window.innerWidth >= 1280);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);
	const { setOpen: openCreateTicket } = useModal('createTicket');
	const { setOpen: openCreateWorkOrder } = useModal('createWorkOrder');

	// Filters
	const [search, setSearch] = useState('');
	const [statusFilter, setStatusFilter] = useState(statusFromUrl || 'active');
	const [priorityFilter, setPriorityFilter] = useState('all');

	// Mobile infinite scroll
	const MOBILE_PAGE_SIZE = 10;
	const [mobileItemsToShow, setMobileItemsToShow] = useState(MOBILE_PAGE_SIZE);
	const observerRef = useRef(null);

	// Reset mobile pagination when filters change
	useEffect(() => {
		setMobileItemsToShow(MOBILE_PAGE_SIZE);
	}, [search, statusFilter, priorityFilter]);

	// Callback ref for infinite scroll - sets up observer when element mounts
	const loadMoreRef = useCallback((node) => {
		if (observerRef.current) {
			observerRef.current.disconnect();
		}

		if (node) {
			observerRef.current = new IntersectionObserver(
				(entries) => {
					if (entries[0].isIntersecting) {
						setMobileItemsToShow((prev) => prev + MOBILE_PAGE_SIZE);
					}
				},
				{ root: null, rootMargin: '100px', threshold: 0 },
			);
			observerRef.current.observe(node);
		}
	}, []);

	// Data
	const {
		value: ticketsData,
		execute: refreshTickets,
		status: loadingStatus,
	} = useAsync(getTickets, {
		defaultPayload: { pageSize: null },
	});

	useEffect(() => {
		refreshTickets({ pageSize: null });
	}, []);

	// Process tickets (search and filter only, sorting handled by DataTable)
	const processedTickets = useMemo(() => {
		let result = ticketsData?.rows || [];

		// Asset filter from URL (hidden filter)
		if (assetIdFromUrl) {
			result = result.filter((t) => t.assetId === assetIdFromUrl);
		}

		// Search
		if (search) {
			const q = search.toLowerCase();
			result = result.filter(
				(t) =>
					t.ticketCode?.toLowerCase().includes(q) ||
					t.subject?.toLowerCase().includes(q),
			);
		}

		// Status filter
		if (statusFilter === 'active') {
			result = result.filter(
				(t) => t.status === 'incoming' || t.status === 'inprogress',
			);
		} else if (statusFilter !== 'all') {
			result = result.filter((t) => t.status === statusFilter);
		}

		// Priority filter
		if (priorityFilter !== 'all') {
			result = result.filter((t) => t.severity === priorityFilter);
		}

		return result;
	}, [ticketsData, search, statusFilter, priorityFilter, assetIdFromUrl]);

	const handleRowClick = (ticket) => {
		navigate(`/tickets-beta/${ticket.id}`);
		openTicketOverview({
			id: ticket.id,
			onClose: (path) => {
				navigate(path || '/tickets-beta');
				refreshTickets({ pageSize: null });
			},
		});
	};

	// Open ticket from URL if ID is present
	useEffect(() => {
		if (ticketIdFromUrl) {
			openTicketOverview({
				id: ticketIdFromUrl,
				onClose: () => {
					navigate('/tickets-beta');
					refreshTickets({ pageSize: null });
				},
			});
		}
	}, [ticketIdFromUrl]);

	const total = ticketsData?.rows?.length || 0;
	const open =
		ticketsData?.rows?.filter((t) => t.status === 'incoming').length || 0;
	const inProgress =
		ticketsData?.rows?.filter((t) => t.status === 'inprogress').length || 0;
	const resolved =
		ticketsData?.rows?.filter(
			(t) => t.status === 'done' || t.status === 'closed',
		).length || 0;

	const isLoading = loadingStatus === 'pending';

	// Mobile card component for each ticket
	const TicketCard = ({ ticket }) => (
		<div
			className="bg-white border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-gray-300 transition-colors"
			onClick={() => handleRowClick(ticket)}
		>
			<div className="flex items-start justify-between mb-2">
				<span className="text-xs text-gray-500 font-mono">
					{ticket.ticketCode || `TK-${ticket.id?.slice(-4)}`}
				</span>
				<StatusBadge status={ticket.status} />
			</div>
			<h3 className="text-sm font-medium text-gray-900 mb-2">
				{ticket.subject || 'No subject'}
			</h3>
			<div className="flex flex-wrap gap-2 mb-3">
				<span className="text-xs text-gray-500">
					{ticket.category?.name || 'Uncategorized'}
				</span>
				{ticket.assignedTo && (
					<>
						<span className="text-xs text-gray-300">•</span>
						<span className="text-xs text-gray-500">
							{getUserFullName(ticket.assignedTo)}
						</span>
					</>
				)}
			</div>
			{ticket?.asset && (
				<div className="text-xs text-gray-400 mb-3">
					{ticket?.asset?.manufacturer?.name} {ticket?.asset?.model?.name} S/N:{' '}
					{ticket?.asset?.serialNumber}
				</div>
			)}
			<div className="flex items-center justify-between pt-3 border-t border-gray-100">
				<div className="flex items-center gap-2">
					{['done', 'closed'].includes(ticket.status) ? (
						<Badge variant="muted">{ticket.severity || 'None'}</Badge>
					) : (
						<PriorityBadge priority={ticket.severity} />
					)}
					<span className="text-xs text-gray-400">
						{ticket.createdBy ? getUserFullName(ticket.createdBy) : 'Unknown'}
					</span>
				</div>
				<span className="text-xs text-gray-400">
					{moment(ticket.createdAt).format('MMM D, YYYY')}
				</span>
			</div>
		</div>
	);

	const columns = [
		{
			key: 'ticketCode',
			header: 'ID',
			width: '100px',
			render: (ticket) => (
				<span className="text-xs text-gray-500 font-mono">
					{ticket.ticketCode || `TK-${ticket.id?.slice(-4)}`}
				</span>
			),
		},
		{
			key: 'status',
			header: 'Status',
			width: '120px',
			render: (ticket) => <StatusBadge status={ticket.status} />,
		},
		{
			key: 'subject',
			header: '',
			render: (ticket) => (
				<TextStack
					primary={ticket.subject}
					primaryFallback="No subject"
					secondary={[
						ticket.category?.name || 'Uncategorized',
						ticket.assignedTo
							? getUserFullName(ticket.assignedTo)
							: 'Unassigned',
						ticket?.asset
							? `${ticket?.asset?.manufacturer?.name} ${ticket?.asset?.model?.name} S/N: ${ticket?.asset?.serialNumber}`
							: null,
					]}
					capitalize
				/>
			),
		},
		{
			key: 'createdBy',
			header: 'Reported By',
			width: '140px',
			sortable: false,
			render: (ticket) => (
				<span className="text-xs text-gray-700">
					{ticket.createdBy ? getUserFullName(ticket.createdBy) : 'Unknown'}
				</span>
			),
		},
		{
			key: 'severity',
			header: 'Priority',
			width: '100px',
			render: (ticket) =>
				['done', 'closed'].includes(ticket.status) ? (
					<Badge variant="muted">{ticket.severity || 'None'}</Badge>
				) : (
					<PriorityBadge priority={ticket.severity} />
				),
		},
		// Only show Created column on xl screens and above
		...(isXl
			? [
					{
						key: 'createdAt',
						header: 'Created',
						width: '260px',
						render: (ticket) => (
							<span className="text-xs text-gray-500">
								{moment(ticket.createdAt).format('ddd, MMM D, YYYY [at] h:mmA')}
							</span>
						),
					},
				]
			: []),
	];

	return (
		<MainContent maxWidth={'w-full'}>
			<PageHeading
				actions={[
					{
						name: 'Create Ticket',
						onClick: () =>
							openCreateTicket({
								onSuccess: () => refreshTickets({ pageSize: null }),
							}),
					},
				]}
				divider={false}
			>
				Tickets
			</PageHeading>

			{/* Stats */}
			<div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
				<StatCard label="Total" value={total} />
				<StatCard label="Open" value={open} valueColor="text-red-600" />
				<StatCard
					label="In Progress"
					value={inProgress}
					valueColor="text-blue-600"
				/>
				<StatCard
					label="Resolved"
					value={resolved}
					valueColor="text-emerald-600"
				/>
			</div>

			{/* Search & Filters */}
			<div className="bg-white border border-gray-200 rounded-lg mb-6">
				<div className="flex flex-col lg:flex-row lg:items-center gap-4 p-4">
					{/* Search */}
					<div className="flex-1 relative">
						<MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
						<input
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Search tickets..."
							className="w-full pl-12 pr-4 py-3 text-sm bg-transparent border border-gray-200 lg:border-0 rounded-lg lg:rounded-none focus:outline-none focus:ring-0 placeholder:text-gray-400"
						/>
					</div>

					{/* Divider - hidden on mobile */}
					<div className="hidden lg:block w-px h-8 bg-gray-200" />

					{/* Filters */}
					<div className="flex items-center gap-3">
						<AdjustmentsHorizontalIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
						<FilterDropdown
							label="Status"
							placeholder="All Status"
							value={statusFilter}
							options={[
								{ value: 'active', label: 'Active' },
								{ value: 'all', label: 'All Status' },
								{ value: 'incoming', label: 'Open' },
								{ value: 'inprogress', label: 'In Progress' },
								{ value: 'done', label: 'Resolved' },
								{ value: 'closed', label: 'Closed' },
							]}
							onChange={setStatusFilter}
						/>
						<FilterDropdown
							label="Priority"
							placeholder="All Priority"
							value={priorityFilter}
							options={[
								{ value: 'all', label: 'All Priority' },
								{ value: 'critical', label: 'Critical' },
								{ value: 'major', label: 'High' },
								{ value: 'minor', label: 'Medium' },
								{ value: 'low', label: 'Low' },
							]}
							onChange={setPriorityFilter}
						/>
					</div>
				</div>
			</div>

			{/* Mobile/Tablet Cards */}
			<div className="lg:hidden space-y-3">
				{isLoading ? (
					<div className="text-center py-8 text-gray-500">Loading...</div>
				) : processedTickets.length === 0 ? (
					<div className="text-center py-8">
						<p className="text-gray-900 font-medium">No tickets found</p>
						<p className="text-gray-500 text-sm">
							Try adjusting your search or filter criteria
						</p>
					</div>
				) : (
					<>
						{processedTickets.slice(0, mobileItemsToShow).map((ticket) => (
							<TicketCard key={ticket.id} ticket={ticket} />
						))}
						{/* Load more sentinel */}
						{mobileItemsToShow < processedTickets.length && (
							<div
								ref={loadMoreRef}
								className="text-center py-4 text-gray-400 text-sm"
							>
								Loading more...
							</div>
						)}
					</>
				)}
			</div>

			{/* Desktop Table */}
			<div className="hidden lg:block">
				<DataTable
					data={processedTickets}
					columns={columns}
					keyField="id"
					loading={isLoading}
					onRowClick={handleRowClick}
					defaultSort={{ key: 'createdAt', direction: 'desc' }}
					containerClassName="border border-gray-200 rounded-lg"
					emptyTitle="No tickets found"
					emptyDescription="Try adjusting your search or filter criteria"
					compact
					pagination
					pageSizeOptions={[10, 25, 50]}
				/>
			</div>
		</MainContent>
	);
};

export default TicketsBeta;
